import debounce from 'lodash/debounce';
import { triggerEvent, documentReady, hasParent } from '../util/dom';
import { expand, collapse } from '../util/animate';
import keys from '../util/keys';

const docBody = document.body;
const header = document.querySelector('header.banner');
const navButton = header.querySelector('nav button');
const menu = navButton.nextElementSibling;
const subMenuContainers = [].slice.call(menu.querySelectorAll('.menu-item-has-children'));
const topLevelsubMenuContainers = [].slice.call(menu.querySelectorAll('.nav > .menu-item-has-children'));
let menuDisplay;

/**
 * Toggle mobile menu and update aria attributes
 */
navButton.addEventListener('click', function() {
    const expanded = this.getAttribute('aria-expanded') === 'true';
    const shouldExpand = !expanded;

    if (shouldExpand) {
        openMenu();
    } else {
        closeMenu();
        this.focus();
    }
});

/**
 * Update aria attributes and classes for sub menu toggles
 * based on mouse/touch and keyboard interactions
 */
subMenuContainers.forEach(function(container, index) {
    const expandButton = container.firstElementChild;
    const subMenuId = `sub-menu-${index}`;
    expandButton.setAttribute('id', subMenuId);
    expandButton.setAttribute('aria-haspopup', true);
    expandButton.setAttribute('aria-expanded', false);

    const buttonCaret = document.createElement('span');
    buttonCaret.classList.add('caret');
    expandButton.appendChild(buttonCaret);

    const subMenu = expandButton.nextElementSibling;
    subMenu.setAttribute('aria-labelledby', subMenuId);

    expandButton.addEventListener('click', function(event) {
        toggleSubMenu(expandButton, subMenu, container);
        event.preventDefault();
    });

    container.addEventListener('keydown', function(event) {
        const button = this.firstElementChild;

        switch(event.keyCode) {
            case keys.space:
                triggerEvent(button, 'click');
                break;
            case keys.esc:
                const expanded = button.getAttribute('aria-expanded') === 'true';
                if (document.activeElement !== button) {
                    button.focus();
                }
                if (expanded) {
                    triggerEvent(button, 'click');
                    event.stopPropagation();
                }
                break;
        }
    });
});

/**
 * Close sub menu when focus leaves top-level nav item
 */
topLevelsubMenuContainers.forEach(function(container) {
    const button = container.firstElementChild;

    // close sub menu when user tabs or clicks out of the area
    container.addEventListener('focusout', function(event) {
        const expanded = button.getAttribute('aria-expanded') === 'true';

        // focusout event triggered each time descendant focus changes
        // and activeElement is set to body element. wait for next
        // event loop and check activeElement at that point
        setTimeout(function() {
            if (expanded && !hasParent(document.activeElement, container)) {
                triggerEvent(button, 'click');
            }
        }, 1);
    });
});

function openMenu() {
    docBody.classList.add('nav-open');
    menu.classList.remove('invisible');
    menu.hidden = false;
    navButton.setAttribute('aria-expanded', true);
    docBody.addEventListener('click', handleBodyClicks);
    header.addEventListener('keydown', handleEscapingMenu);
}

function closeMenu() {
    docBody.classList.remove('nav-open');
    menu.hidden = true;
    navButton.setAttribute('aria-expanded', false);
    docBody.removeEventListener('click', handleBodyClicks);
    header.removeEventListener('keydown', handleEscapingMenu);
}

function toggleSubMenu(expandButton, subMenu, container) {
    const expanded = expandButton.getAttribute('aria-expanded') === 'true';
    const shouldExpand = !expanded;
    expandButton.setAttribute('aria-expanded', shouldExpand);

    if (shouldExpand) {
        expand(subMenu);
        container.classList.add('show');
        subMenu.classList.add('show');
    } else {
        collapse(subMenu);
        container.classList.remove('show');
        subMenu.classList.remove('show');
    }
}

function handleBodyClicks(event) {
    var target = event.target;

    if (!hasParent(target, header)) {
        triggerEvent(navButton, 'click');
    }
}

function handleEscapingMenu(event) {
    if (event.keyCode === keys.esc) {
        triggerEvent(navButton, 'click');
    }
}

function setAriaActivePage(currentPage, activePageLinks) {
    activePageLinks.forEach(function(link) {
        if (link.pathname === currentPage) {
            link.setAttribute('aria-current', 'page');
        }
    });
}

// We assume that when the menu has a position of "absolute"
// it's in it's mobile view.
function isMenuMobile() {
    return window.getComputedStyle(menu).position === 'absolute';
}

function setMenuAttributes() {
    const isMobile = isMenuMobile();

    if (menuDisplay === isMobile) {
        return;
    }

    menuDisplay = isMobile;

    // removes event handlers and does some other cleanup
    closeMenu();

    navButton.setAttribute('aria-expanded', !isMobile);
    menu.hidden = isMobile;
    isMobile ? menu.classList.add('invisible') : menu.classList.remove('invisible');
}

// Nav should be hidden on page load
// Add additional setTimeout for setMenuAttributes to work with HMR
// during development
documentReady(() => {
    setMenuAttributes();

    if (module.hot) {
        setTimeout(setMenuAttributes, 300);
    }
});

// Check for links for the current page and set proper aria attribute
setAriaActivePage(window.location.pathname, [].slice.call(menu.querySelectorAll('.active > a')));

// Prevent tabbing from moving to nav menu when off screen
menu.addEventListener('transitionend', function() {
    if (this.hidden) {
        this.classList.add('invisible');
    }
});

window.addEventListener('resize', debounce(setMenuAttributes, 200));
